import { Fragment, useEffect, useMemo, useState } from 'react'
import { Icon, type IconName } from './icon'
import { T } from '../data/mock'
import type { NavId } from './layout/top-bar'

type ActionId = 'new-tx'

type Props = {
  onClose: () => void
  onNavigate: (id: NavId | 'accounts') => void
  onAction: (id: ActionId) => void
}

type Item = {
  group: string
  icon: IconName
  label: string
  shortcuts: string[]
  run: () => void
}

export function CmdK({ onClose, onNavigate, onAction }: Props) {
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)

  const items: Item[] = useMemo(
    () => [
      {
        group: 'Дії',
        icon: 'plus',
        label: T.buttons.new_tx,
        shortcuts: ['N'],
        run: () => onAction('new-tx'),
      },
      { group: 'Дії', icon: 'upload', label: 'Імпорт виписки', shortcuts: [], run: () => {} },
      { group: 'Дії', icon: 'download', label: 'Експорт у CSV', shortcuts: [], run: () => {} },
      { group: 'Перейти', icon: 'layout-dashboard', label: T.nav.dashboard, shortcuts: ['G', 'D'], run: () => onNavigate('dashboard') },
      { group: 'Перейти', icon: 'wallet', label: T.nav.accounts, shortcuts: ['G', 'A'], run: () => onNavigate('accounts') },
      { group: 'Перейти', icon: 'arrow-left-right', label: T.nav.transactions, shortcuts: ['G', 'T'], run: () => onNavigate('transactions') },
      { group: 'Перейти', icon: 'trending-up', label: T.nav.forecast, shortcuts: ['G', 'F'], run: () => onNavigate('forecast') },
      { group: 'Перейти', icon: 'pie-chart', label: T.nav.cashflow, shortcuts: ['G', 'C'], run: () => onNavigate('cashflow') },
      { group: 'Перейти', icon: 'key-round', label: T.nav.licenses, shortcuts: ['G', 'L'], run: () => onNavigate('licenses') },
      { group: 'Перейти', icon: 'settings', label: T.nav.settings, shortcuts: ['G', 'S'], run: () => onNavigate('settings') },
    ],
    [onNavigate, onAction],
  )

  const filtered = q.trim()
    ? items.filter((it) => it.label.toLowerCase().includes(q.toLowerCase()))
    : items

  const groups = useMemo(() => {
    const out: Record<string, Item[]> = {}
    for (const it of filtered) {
      ;(out[it.group] = out[it.group] ?? []).push(it)
    }
    return out
  }, [filtered])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((a) => Math.min(a + 1, filtered.length - 1))
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((a) => Math.max(a - 1, 0))
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        filtered[active]?.run()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [filtered, active, onClose])

  let cursor = 0
  return (
    <>
      <div className="cmdk-scrim" onClick={onClose} />
      <div className="cmdk" role="dialog" aria-modal="true">
        <div className="cmdk-search">
          <Icon name="search" size={16} color="var(--fg-4)" />
          <input
            autoFocus
            placeholder="Знайти операцію, рахунок, звіт…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
          />
          <span className="kbd">esc</span>
        </div>
        <div className="cmdk-list">
          {Object.entries(groups).map(([group, list]) => (
            <Fragment key={group}>
              <div className="cmdk-group-h">{group}</div>
              {list.map((it) => {
                const idx = cursor++
                return (
                  <div
                    key={it.label}
                    className={`cmdk-item ${idx === active ? 'active' : ''}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => {
                      it.run()
                      onClose()
                    }}
                  >
                    <Icon name={it.icon} size={14} />
                    <span>{it.label}</span>
                    <span className="shortcuts">
                      {it.shortcuts.map((k, i) => (
                        <span key={i} className="kbd">
                          {k}
                        </span>
                      ))}
                    </span>
                  </div>
                )
              })}
            </Fragment>
          ))}
          {filtered.length === 0 && (
            <div
              style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}
            >
              Нічого не знайдено.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
